import {sequential, Sequential, tensor2d, tensor4d, Tensor, tidy, layers, LayersModel, train, io, loadLayersModel} from "@tensorflow/tfjs";
import {toRaw, isProxy} from "vue";

async function cloneNetwork(source: Sequential): Promise<Sequential> {
	let artifacts: any = null;
	await source.save(io.withSaveHandler(async (a) => {
		artifacts = a;
		return {modelArtifactsInfo: {dateSaved: new Date(), modelTopologyType: 'JSON'}};
	}));
	const clone = await loadLayersModel({load: async () => artifacts});
	clone.setWeights(source.getWeights());
	return clone as Sequential;
}

export interface ModelOptions {
	numStates: number;
	numActions: number;
	batchSize: number;
	network?: LayersModel;
	learningRate?: number;
	epsilon?: number;
	minEpsilon?: number;
	epsilonDecay?: number;
	targetSyncEvery?: number;
	boardSize?: number;
}

export class Model {
	readonly numStates: number;
	readonly numActions: number;
	readonly batchSize: number;
	readonly inputShape: number[];
	network: Sequential;
	readonly targetSyncEvery: number;
	private targetNetwork: Sequential | null = null;
	private targetNetworkReady: Promise<Sequential> | null = null;
	private trainingsSinceSync: number = 0;
	private epsilon: number;
	private readonly minEpsilon: number;
	private readonly epsilonDecay: number;

	constructor(options: ModelOptions) {
		const {
			numStates,
			numActions,
			batchSize,
			network,
			learningRate = 0.001,
			epsilon = 1.0,
			minEpsilon = 0.05,
			epsilonDecay = 0.9995,
			targetSyncEvery = 100,
			boardSize,
		} = options;
		this.numStates = numStates;
		this.numActions = numActions;
		this.batchSize = batchSize;
		this.epsilon = epsilon;
		this.minEpsilon = minEpsilon;
		this.epsilonDecay = epsilonDecay;
		this.targetSyncEvery = targetSyncEvery;
		if (network) {
			this.network = network as Sequential;
		} else if (boardSize) {
			const channels = numStates / (boardSize * boardSize);
			this.network = sequential();

			this.network.add(layers.conv2d({
				inputShape: [boardSize, boardSize, channels],
				filters: 64,
				kernelSize: 2,
				activation: 'relu',
			}));

			this.network.add(layers.conv2d({
				filters: 128,
				kernelSize: 2,
				activation: 'relu',
			}));

			this.network.add(layers.flatten());

			this.network.add(layers.dense({
				units: 256,
				activation: 'relu',
			}));

			this.network.add(layers.dense({
				units: this.numActions,
				activation: 'linear'
			}));
		} else {
			this.network = sequential();

			this.network.add(layers.dense({
				units: 256,
				activation: 'relu',
				inputShape: [this.numStates],
			}));

			this.network.add(layers.dense({
				units: 128,
				activation: 'relu',
			}));

			this.network.add(layers.dense({
				units: 64,
				activation: 'relu',
			}));

			this.network.add(layers.dense({
				units: this.numActions,
				activation: 'linear'
			}));
		}
		this.inputShape = this.network.inputs[0].shape.slice(1) as number[];
		this.network.summary();
		this.network.compile({
			optimizer: train.adam(learningRate),
			loss: 'meanSquaredError'
		});
	}

	private ensureTargetNetwork(): Promise<Sequential> {
		if (!this.targetNetworkReady) {
			this.targetNetworkReady = cloneNetwork(this.network).then(clone => {
				this.targetNetwork = clone;
				return clone;
			});
		}
		return this.targetNetworkReady;
	}

	async syncTarget() {
		const target = await this.ensureTargetNetwork();
		target.setWeights(this.network.getWeights());
	}

	async predictTarget(states: Tensor | Tensor[]): Promise<Tensor> {
		const target = await this.ensureTargetNetwork();
		return tidy(() => target.predict(states) as Tensor);
	}

	predict(states: Tensor | Tensor[]): Tensor {
		return tidy(() => this.network.predict(states) as Tensor);
	}

	makeInputTensor(states: number[][]): Tensor {
		if (this.inputShape.length === 3) {
			const [height, width, channels] = this.inputShape;
			return tensor4d(states.flat(), [states.length, height, width, channels]);
		}
		return tensor2d(states, [states.length, this.numStates]);
	}

	async train(xBatch: Tensor | Tensor[], yBatch: Tensor | Tensor[]) {
		await this.network.fit(xBatch, yBatch, {verbose: 0});
		this.trainingsSinceSync++;
		if (this.trainingsSinceSync >= this.targetSyncEvery) {
			this.trainingsSinceSync = 0;
			await this.syncTarget();
		}
	}

	async saveModel(save_path: string) {
		if (isProxy(this.network)) {
			const unproxied: Sequential = toRaw(this.network);
			return await unproxied.save(save_path);
		} else {
			return await this.network.save(save_path);
		}
	}

	getEpsilon(): number {
		return this.epsilon;
	}

	setEpsilon(value: number) {
		this.epsilon = Math.max(value, this.minEpsilon);
	}

	chooseAction(state: Tensor, legal?: boolean[], greedy: boolean = false): number {
		const candidates = legal ? legal.map((isLegal, i) => isLegal ? i : -1).filter(i => i >= 0) : [];
		const actions = candidates.length > 0 ? candidates : Array.from({length: this.numActions}, (_, i) => i);
		let action: number;
		if (!greedy && Math.random() < this.epsilon) {
			action = actions[Math.floor(Math.random() * actions.length)];
		} else {
			const qValues = this.predict(state);
			const values = qValues.dataSync();
			action = actions[0];
			for (let i = 1; i < actions.length; i++) {
				if (values[actions[i]] > values[action]) {
					action = actions[i];
				}
			}
			qValues.dispose();
		}
		if (!greedy) {
			this.epsilon = Math.max(this.epsilon * this.epsilonDecay, this.minEpsilon);
		}
		return action;
	}
}
